import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container } from '@/components/layout/Container';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/States';
import { RideCard } from '@/features/rides/components/RideCard';
import { RideSearchForm } from '@/features/rides/components/RideSearchForm';
import { useRideRouteSearch } from '@/features/rides/hooks/useRides';
import { toSearchParams } from '@/features/rides/schemas/rideSearch';
import type { RouteSearchParams } from '@/services/endpoints';
import { resolvePlace } from '@/utils/places';

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const date = searchParams.get('date') ?? '';
  const passengers = Number(searchParams.get('passengers') ?? '1') || 1;

  // Matched against drivers' actual routes: your pickup and drop can be anywhere along
  // the way, not only a driver's exact start and end. The backend resolves these typed
  // names to coordinates via the same gazetteer the form's suggestions come from.
  const query = useMemo<RouteSearchParams | undefined>(() => {
    const pickup = resolvePlace(from);
    const drop = resolvePlace(to);
    if (!pickup || !drop || !date) return undefined;
    return { pickupLat: pickup.latitude, pickupLng: pickup.longitude,
      dropLat: drop.latitude, dropLng: drop.longitude, date, passengers };
  }, [from, to, date, passengers]);

  const results = useRideRouteSearch(query);

  return (
    <Container>
      <PageHeader
        title={from && to ? `${from} to ${to}` : 'Find a ride'}
        description="Rides are matched by how close your pickup and drop-off sit to the driver's route, not by exact place names."
      />
      <Card className="mb-8"><CardBody>
        <RideSearchForm
          defaultValues={{ from, to, ...(date ? { date } : {}), passengers }}
          submitting={results.isFetching}
          onSearch={(values) => setSearchParams(toSearchParams(values))}
        />
      </CardBody></Card>

      {!query ? (
        <EmptyState title="Where to?">Choose where you are leaving from, where you are going, and a date.</EmptyState>
      ) : results.isPending ? (
        <LoadingState label="Searching rides" />
      ) : results.isError ? (
        <ErrorState error={results.error} onRetry={() => void results.refetch()} />
      ) : results.data.items.length === 0 ? (
        <EmptyState title="No rides found for that day">
          Try another date, or offer the trip yourself.
        </EmptyState>
      ) : (
        <ul className="space-y-4" aria-label="Available rides">
          {results.data.items.map((ride) => (
            <li key={ride.rideId}><RideCard ride={ride} segment={{ pickup: from, drop: to }} /></li>
          ))}
        </ul>
      )}
    </Container>
  );
}
